import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkChangeEventDialog } from 'src/dialogs/BulkEdits/BulkChangeEventDialog';
import { TicketGroupIntentionDialog } from 'src/dialogs/TicketGroupIntentionDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserCanUpdateListing } from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingDetails,
  ListingQuery,
  TicketGroupEditReason,
} from 'src/WebApiController';

import { INVENTORY_BULK_CHANGE_EVENT_LISTINGS_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchChangeEvent } from './LaunchChangeEvent';

export const useBulkChangeEventAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasPermission = useUserCanUpdateListing(undefined, true);

  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `inventory-changeEvent-event-${event.virtId}`
    : INVENTORY_BULK_CHANGE_EVENT_LISTINGS_KEY;

  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();

  const {
    refreshCatalog,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { refreshMetrics } = useCatalogMetricsContext();
  const [changeEventNote, setChangeEventNote] = useState<string | undefined>();
  const deallocateSalesRef = useRef(true);

  const { showErrorDialog } = useClientLogContext();

  const ticketGroupIntentionDialog = useBasicDialog();

  const {
    openDialog: openBulkChangeEventDialog,
    closeDialog: closeBulkChangeEventDialog,
  } = useDialog(DialogId.BulkChangeEvent, BulkChangeEventDialog);

  const onSubmitChangeEvent = useCallback(
    async (
      newSelectedEvent: Event | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);

      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const preview = await client.bulkChangeEventPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkChangeEventPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (newSelectedEvent) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkChangeEvent(
              preview!.preview,
              newSelectedEvent.posId!,
              newSelectedEvent.viagId,
              newSelectedEvent.mpId,
              deallocateSalesRef.current,
              changeEventNote ?? '',
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and load latest info
                setActivePosEntity(0);
                await refreshCatalog();
                await refreshMetrics();
                refreshExpandedListItems();
              }

              closeBulkChangeEventDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitChangeEvent', error, {
              trackErrorData: {
                newSelectedEvent,
                preview,
                changeEventNote,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      changeEventNote,
      closeBulkChangeEventDialog,
      filterQueryWithEventIds,
      refreshCatalog,
      refreshExpandedListItems,
      refreshMetrics,
      setActivePosEntity,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const onChangeEventClick = useCallback(
    (changeEventNote?: string) => {
      setChangeEventNote(changeEventNote);

      openBulkChangeEventDialog({
        entityType: ActionOutboxEntityType.Listing,
        changeEventNote,
        updateKey,
        onOkay: onSubmitChangeEvent,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkChangeEventDialog,
      });
    },
    [
      closeBulkChangeEventDialog,
      onSubmitChangeEvent,
      openBulkChangeEventDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkChangeEventDialog({
      entityType: ActionOutboxEntityType.Listing,
      changeEventNote,
      updateKey,
      onOkay: onSubmitChangeEvent,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkChangeEventDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditChangeEvent,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditChangeEvent,
        disabled: !hasPermission,
      });
    };
  }, [hasPermission, openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPermission) {
      return [];
    }

    return [
      {
        disabled: isLoading,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          ticketGroupIntentionDialog.launchDialog();
        },
        title: ContentId.ChangeEvent,
      },
    ];
  }, [hasPermission, isLoading, ticketGroupIntentionDialog]);

  return {
    dropDown: (
      <LaunchChangeEvent
        key="ChangeEvent"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          ticketGroupIntentionDialog.launchDialog();
        }}
        disabled={isLoading}
      />
    ),
    actionItems,
    dialog: (
      <>
        <TicketGroupIntentionDialog
          {...ticketGroupIntentionDialog.dialogProps}
          intention={{
            ticketGroupId: -1, // negative since this will be applied to ticket groups in bulk
            reason: TicketGroupEditReason.EditEvent,
            additionalInfo: '',
          }}
          onClosed={ticketGroupIntentionDialog.closeDialog}
          onSave={(i, deallocateSales) => {
            deallocateSalesRef.current = deallocateSales == true;
            onChangeEventClick(i.additionalInfo);
            ticketGroupIntentionDialog.closeDialog();
          }}
          saveButtonContentId={ContentId.Save}
        />
      </>
    ),
  };
};
